import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Loader2, AlertCircle, MapPin } from 'lucide-react';
import { TextInput } from '../../../components/common/TextInput';
import { LocationModal, LocationData } from '../../../../shared/components/location/LocationModalPortal';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '@portal/hooks/useAccount';
import type { PublicForm } from '../../../types/publicForm.types';

interface FormBuiltinFieldsProps {
  form: PublicForm;
  formData: Record<string, any>;
  onUpdateField: (field: string, value: any) => void;
  onContinue: () => void;
  onBack: () => void;
  primaryColor: string;
  isSaving?: boolean;
}

interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  phone?: string;
  address?: string;
}

export const FormBuiltinFields: React.FC<FormBuiltinFieldsProps> = ({
  form,
  formData,
  onUpdateField,
  onContinue,
  onBack,
  primaryColor,
  isSaving = false,
}) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isValidating, setIsValidating] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [hasAutoPopulated, setHasAutoPopulated] = useState(false);
  const navigate = useNavigate();

  // Get account data for auto-population
  const { data: account, isLoading: isAccountLoading } = useAccount();

  // Form settings - use options field as per the API response
  const showCompany = form.options?.showCompanyOnForm === '1';
  const requireCompany = form.options?.requireCompany === '1';
  const showPhone = form.options?.showPhoneOnForm === '1';
  const requirePhone = form.options?.requirePhone === '1';
  const showAddress = form.options?.showAddressOnForm === '1';
  const requireAddress = form.options?.requireAddress === '1';

  // Auto-populate fields with account data when available
  useEffect(() => {
    // Only auto-populate once and when account data is available
    if (!hasAutoPopulated && account && !isAccountLoading) {
      const fieldsToUpdate: Record<string, string> = {};

      // Auto-populate firstName if empty
      if (!formData.firstName && (account.firstName || account.profile?.firstName)) {
        fieldsToUpdate.firstName = account.firstName || account.profile?.firstName || '';
      }

      // Auto-populate lastName if empty
      if (!formData.lastName && (account.lastName || account.profile?.lastName)) {
        fieldsToUpdate.lastName = account.lastName || account.profile?.lastName || '';
      }

      // Auto-populate email if empty
      if (!formData.email && account.email) {
        fieldsToUpdate.email = account.email;
      }

      // Auto-populate phone if empty and phone field is shown
      if (showPhone && !formData.phone && (account.phone || account.profile?.phone)) {
        fieldsToUpdate.phone = account.phone || account.profile?.phone || '';
      }

      // Auto-populate company if empty and company field is shown
      // Check for active company membership first, then fall back to profile company
      if (showCompany && !formData.company) {
        // Try to get company name from active company membership
        const companyName = account.activeCompanyMembership?.companyName || 
                           account.profile?.company || 
                           '';
        if (companyName) {
          fieldsToUpdate.company = companyName;
        }
      }

      // Apply all updates if any fields need to be populated
      if (Object.keys(fieldsToUpdate).length > 0) {
        Object.entries(fieldsToUpdate).forEach(([field, value]) => {
          onUpdateField(field, value);
        });
      }

      setHasAutoPopulated(true);
    }
  }, [account, isAccountLoading, hasAutoPopulated, formData, showPhone, showCompany, onUpdateField]);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateField = useCallback((field: string, value: any): string | undefined => {
    switch (field) {
      case 'firstName':
        if (!value?.trim()) return 'First name is required';
        break;
      case 'lastName':
        if (!value?.trim()) return 'Last name is required';
        break;
      case 'email':
        if (!value?.trim()) return 'Email is required';
        if (!validateEmail(value)) return 'Please enter a valid email address';
        break;
      case 'company':
        if (requireCompany && !value?.trim()) return 'Company is required';
        break;
      case 'phone':
        if (requirePhone && !value?.trim()) return 'Phone is required';
        break;
      case 'address':
        if (requireAddress) {
          if (!formData.address1?.trim() || !formData.city?.trim() ||
              !formData.state?.trim() || !formData.zip?.trim()) {
            return 'Complete address is required';
          }
        }
        break;
    }
    return undefined;
  }, [requireCompany, requirePhone, requireAddress, formData]);

  // Validate all fields
  const validateAllFields = useCallback((): boolean => {
    const newErrors: ValidationErrors = {};

    // Always validate required fields
    const firstNameError = validateField('firstName', formData.firstName);
    if (firstNameError) newErrors.firstName = firstNameError;

    const lastNameError = validateField('lastName', formData.lastName);
    if (lastNameError) newErrors.lastName = lastNameError;

    const emailError = validateField('email', formData.email);
    if (emailError) newErrors.email = emailError;

    // Validate optional fields if shown
    if (showCompany) {
      const companyError = validateField('company', formData.company);
      if (companyError) newErrors.company = companyError;
    }

    if (showPhone) {
      const phoneError = validateField('phone', formData.phone);
      if (phoneError) newErrors.phone = phoneError;
    }

    if (showAddress) {
      const addressError = validateField('address', null);
      if (addressError) newErrors.address = addressError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateField, showCompany, showPhone, showAddress]);

  // Handle field change
  const handleFieldChange = (field: string, value: string) => {
    onUpdateField(field, value);

    // Clear error if field becomes valid
    if (touched[field]) {
      const error = validateField(field, value);
      setErrors(prev => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[field] = error;
        } else {
          // Remove the error completely if field is valid
          delete newErrors[field];
        }
        return newErrors;
      });
    }
  };

  // Handle field blur
  const handleFieldBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[field] = error;
      } else {
        // Remove the error if field is valid
        delete newErrors[field];
      }
      return newErrors;
    });
  };

  // Handle location save
  const handleLocationSave = (location: LocationData) => {
    // Map LocationData fields to form fields
    // Note: venueName maps to the name field in the address model
    const addressFields = {
      name: location.venueName,
      address1: location.streetAddress,
      address2: location.streetAddress2 || '',
      city: location.city,
      state: location.stateRegion,
      zip: location.postalCode,
      country: location.country || 'US'
    };
    
    // Check if onUpdateField accepts a third parameter for batched updates
    // This allows the parent to handle all address fields atomically
    if (onUpdateField.length >= 3) {
      // Call with batched update - send all address fields at once
      onUpdateField('address1', addressFields.address1, addressFields);
    } else {
      // Fallback to individual updates for compatibility
      Object.entries(addressFields).forEach(([field, value]) => {
        onUpdateField(field, value);
      });
    }
    
    setShowLocationModal(false);

    // Mark address as touched since user interacted with it
    setTouched(prev => ({ ...prev, address: true }));

    // Clear the address error immediately if we have all required fields
    if (requireAddress && location.streetAddress && location.city &&
        location.stateRegion && location.postalCode) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.address;
        return newErrors;
      });
    }
  };

  // Handle continue
  const handleContinue = async () => {
    setIsValidating(true);

    // Mark all fields as touched
    const allTouched: Record<string, boolean> = {
      firstName: true,
      lastName: true,
      email: true,
    };
    if (showCompany) allTouched.company = true;
    if (showPhone) allTouched.phone = true;
    if (showAddress) allTouched.address = true;

    setTouched(allTouched);

    // Validate all fields
    const isValid = validateAllFields();

    setIsValidating(false);

    if (isValid) {
      onContinue();
    } else {
      // Scroll to first error
      setTimeout(() => {
        const firstErrorElement = document.querySelector('.text-red-600');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Format address for display
  const formatAddress = () => {
    const parts = [
      formData.name,
      formData.address1,
      formData.address2,
      formData.city,
      formData.state,
      formData.zip,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : 'Click to add address';
  };

  // Handle exit
  const handleExit = () => {
    // Check if any data has been entered
    const hasData = formData.firstName || formData.lastName || formData.email ||
                   formData.company || formData.phone || formData.address1;

    if (hasData) {
      setShowExitConfirmation(true);
    } else {
      navigate('/forms');
    }
  };

  const confirmExit = () => {
    // Clear any draft data if needed
    navigate('/forms');
  };

  // Handle keyboard events for exit confirmation dialog
  useEffect(() => {
    if (!showExitConfirmation) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        confirmExit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowExitConfirmation(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showExitConfirmation]);

  return (
    <>
      {/* Exit button - consistent with other form components */}
      <button
        onClick={handleExit}
        className="flex items-center text-gray-600 hover:text-gray-900 bg-white px-3 py-1.5 rounded-lg shadow-sm mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Exit
      </button>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Contact Information</h2>
          <p className="text-gray-600 mb-6">
            Please provide your contact information below to continue.
          </p>

          {/* Error Summary */}
          {Object.keys(errors).length > 0 && Object.values(touched).some(t => t) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Please correct the following:</h3>
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                    {Object.entries(errors)
                      .filter(([_, error]) => error) // Filter out undefined/empty errors
                      .map(([field, error]) => (
                        <li key={field}>{error}</li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Name fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                label="First Name"
                required
                value={formData.firstName || ''}
                onChange={(value) => handleFieldChange('firstName', value)}
                onBlur={() => handleFieldBlur('firstName')}
                error={touched.firstName ? errors.firstName : undefined}
                placeholder="Enter your first name"
                autoComplete="given-name"
                name="firstName"
              />
              <TextInput
                label="Last Name"
                required
                value={formData.lastName || ''}
                onChange={(value) => handleFieldChange('lastName', value)}
                onBlur={() => handleFieldBlur('lastName')}
                error={touched.lastName ? errors.lastName : undefined}
                placeholder="Enter your last name"
                autoComplete="family-name"
                name="lastName"
              />
            </div>

            {/* Email field */}
            <TextInput
              label="Email"
              type="email"
              required
              value={formData.email || ''}
              onChange={(value) => handleFieldChange('email', value)}
              onBlur={() => handleFieldBlur('email')}
              error={touched.email ? errors.email : undefined}
              placeholder="your.email@example.com"
              autoComplete="email"
              name="email"
            />

            {/* Company field */}
            {showCompany && (
              <TextInput
                label="Company"
                required={requireCompany}
                value={formData.company || ''}
                onChange={(value) => handleFieldChange('company', value)}
                onBlur={() => handleFieldBlur('company')}
                error={touched.company ? errors.company : undefined}
                placeholder="Enter your company name"
                autoComplete="organization"
                name="company"
              />
            )}

            {/* Phone field */}
            {showPhone && (
              <TextInput
                label="Phone"
                type="tel"
                required={requirePhone}
                value={formData.phone || ''}
                onChange={(value) => handleFieldChange('phone', value)}
                onBlur={() => handleFieldBlur('phone')}
                error={touched.phone ? errors.phone : undefined}
                placeholder="(555) 123-4567"
                autoComplete="tel"
                name="phone"
              />
            )}

            {/* Address field */}
            {showAddress && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                  {requireAddress && <span className="text-red-500 ml-1">*</span>}
                </label>
                <button
                  type="button"
                  onClick={() => setShowLocationModal(true)}
                  className={`w-full text-left px-4 py-2 border rounded-lg transition-colors ${
                    touched.address && errors.address
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                >
                  <div className="flex items-center justify-between">
                    <span className={formData.address1 ? 'text-gray-900' : 'text-gray-500'}>
                      {formatAddress()}
                    </span>
                    <MapPin className="h-5 w-5 text-gray-400" />
                  </div>
                </button>
                {touched.address && errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                )}
              </div>
            )}
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            <button
              onClick={onBack}
              className="inline-flex items-center px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </button>
            <button
              onClick={handleContinue}
              disabled={isValidating || isSaving}
              className="inline-flex items-center px-6 py-2 text-white rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: primaryColor,
                opacity: isValidating || isSaving ? 0.5 : 1,
              }}
            >
              {isValidating || isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isSaving ? 'Saving...' : 'Validating...'}
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Location Modal */}
      {showAddress && showLocationModal && (
        <LocationModal
          isOpen={showLocationModal}
          onClose={() => setShowLocationModal(false)}
          location={{
            venueName: formData.name || '',
            streetAddress: formData.address1 || '',
            streetAddress2: formData.address2 || '',
            city: formData.city || '',
            stateRegion: formData.state || '',
            postalCode: formData.zip || '',
            country: formData.country || 'US',
          }}
          onSave={handleLocationSave}
        />
      )}

      {/* Exit Confirmation Dialog */}
      {showExitConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Are you sure you want to leave?
            </h3>
            <p className="text-gray-600 mb-6">
              You'll lose any information you've entered and will need to start over.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowExitConfirmation(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmExit}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Leave Form
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};